import { HrGridPaper } from 'routes/HumanResourcesModule/components/HrGridPaper/HrGridPaper';
import React from 'react';
import {
    CardContent,
    CardHeader,
    Paper,
    Skeleton,
    Typography,
    useTheme,
} from '@mui/material';
import { HrGridPaperElement } from 'routes/HumanResourcesModule/components/HrGridPaper/types';
import { Trans, useTranslation } from 'react-i18next';
import {
    useGetIndicatorSalaryMassData,
    useGetIndicatorSupCompData,
} from '../helpers/hooks/payStateData';
import { PayStateT } from '../../helpers/types';
import { currencyFormatter } from 'helpers/formatHelper';
import StyCard from 'routes/HumanResourcesModule/components/ui/StyCard';
import styled from '@mui/system/styled';
import { PayStateIndicatorItem } from './helpers/types';
import HrBarLineChart from 'routes/HumanResourcesModule/components/HrChart/HrBarLineChart';
import { getBarLineEchartOptions } from './helpers/eChartHelpers';
import { MANTY_COLORS } from 'business/constants';
import { FULL_MONTHS } from 'helpers/dateHelper';

const StyHrCard = styled(StyCard)(() => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: '1',
    height: '100%',
    minHeight: '20rem',
}));
const StyPaper = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.primary.background,
    textAlign: 'left',
    padding: '1rem',
    width: '100%',
    marginLeft: '1rem',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: '1',
    justifyContent: 'space-between',
}));
const StyCardContent = styled(CardContent)({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: '100%',
    alignItems: 'stretch',
});
const StyTypography = styled(Typography)(() => ({
    paddingX: '1rem',
    textAlign: 'center',
}));

export const PayStateIndicators = ({ payState }: { payState: PayStateT }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const {
        data: indicatorSalaryMassResponse,
        isLoading: isIndicatorSalaryMassResponseLoading,
    } = useGetIndicatorSalaryMassData(payState.id);
    const { exercice } = payState;

    const {
        data: indicatorSupCompResponse,
        isLoading: isIndicatorSupCompResponseLoading,
    } = useGetIndicatorSupCompData(payState.id);

    const indicatorSalaryMass = indicatorSalaryMassResponse?.data ?? [];
    const indicatorSupComp = indicatorSupCompResponse?.data ?? [];

    const isLoading =
        isIndicatorSalaryMassResponseLoading ||
        isIndicatorSupCompResponseLoading;

    const currentExercice = payState.exercice;
    const colors: string[] = [
        theme.palette.primary.main,
        theme.palette.orange ?? MANTY_COLORS.orange,
    ];
    const now = new Date();
    // the data is reversed in order to get the most recent data first
    const elements: HrGridPaperElement[] = [exercice, exercice - 1].map(
        (e: number) => {
            const startMonth = 0;
            const endMonth =
                now.getFullYear() === payState.exercice
                    ? Math.max(new Date().getMonth() - 1, 0)
                    : 11;
            // Get the filtered value or return 0 if it doesn't exist
            const filteredSalaryMass = Object.values(
                indicatorSalaryMass,
            ).filter((m: PayStateIndicatorItem) => {
                const itemDate = new Date(m.date);
                return (
                    itemDate.getFullYear() === e &&
                    itemDate.getMonth() <= endMonth
                );
            });

            const salarySum: number | undefined =
                filteredSalaryMass.length > 0
                    ? filteredSalaryMass.reduce(
                          (sum, m: PayStateIndicatorItem) =>
                              sum + m.salaryValue,
                          0,
                      )
                    : undefined;
            const displayValue =
                salarySum !== undefined ? currencyFormatter(salarySum) : '-';

            return {
                title: (
                    <Trans
                        i18nKey={`humanResources.payState.${startMonth === endMonth ? 'indicatorPeriodTitleSameMonth' : 'indicatorPeriodTitle'}`}
                        tOptions={{
                            startDate: FULL_MONTHS[startMonth].toLowerCase(),
                            endDate: `${FULL_MONTHS[endMonth].toLowerCase()} ${e}`,
                            month: `${FULL_MONTHS[startMonth].toLowerCase()} ${e}`,
                        }}
                        components={{
                            span: (
                                <span
                                    style={{
                                        color: theme.palette.primary.main,
                                    }}
                                />
                            ),
                        }}
                    />
                ),
                children: <Typography variant="h5">{displayValue}</Typography>,
                isLoading: isLoading,
            };
        },
    );

    return (
        <>
            {isLoading ? (
                <Skeleton
                    variant="rectangular"
                    sx={{
                        height: '20rem',
                        width: '20',
                    }}
                />
            ) : (
                <StyHrCard>
                    <CardHeader
                        titleTypographyProps={{ variant: 'h5' }}
                        title={t(
                            'humanResources.payState.payStateDashboardTitle',
                        )}
                    />
                    <StyCardContent>
                        <HrGridPaper elements={elements} />
                        <StyPaper>
                            <StyTypography variant="body1">
                                {t(
                                    'humanResources.payState.indicatorChartSalaryMassTitle',
                                )}
                            </StyTypography>
                            <HrBarLineChart
                                isLoading={isLoading}
                                options={getBarLineEchartOptions(
                                    colors,
                                    currentExercice,
                                    indicatorSalaryMass,
                                )}
                            />
                        </StyPaper>
                        <StyPaper>
                            <StyTypography variant="body1">
                                {t(
                                    'humanResources.payState.indicatorChartSupCompTitle',
                                )}
                            </StyTypography>
                            <HrBarLineChart
                                isLoading={isLoading}
                                options={getBarLineEchartOptions(
                                    colors,
                                    currentExercice,
                                    indicatorSupComp,
                                )}
                            />
                        </StyPaper>
                    </StyCardContent>
                </StyHrCard>
            )}
        </>
    );
};
